import { Stack, useLocalSearchParams } from 'expo-router';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { requestContentCardsRefresh } from 'services/engagement/braze/braze';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import { useEngagement } from 'shared/hooks/useBraze';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import SuccessScreen from 'shared/patterns/vxl/SuccessScreen/SuccessScreen';
import theme from 'shared/styles/theme';

const MoneyInTheBankScreen = () => {
  const { t: translatedText } = useTranslation('vloc', {
    keyPrefix: 'moneyInTheBank',
  });

  const { moneyInTheBankTitle, moneyInTheBankMessage, takeOutSubCategory } =
    useLocalSearchParams<{
      moneyInTheBankTitle?: string;
      moneyInTheBankMessage?: string;
      takeOutSubCategory: string;
    }>();

  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    moneyInTheBankEvent();
    dismissToTab(Tabs.HOME);
  };

  useBackHandler(goHome);

  useViewDidFocusScreenAnalyticsEvent({
    key: 'moneyInTheBankScreen',
    customAttributes: {
      subcategory:
        takeOutSubCategory === 'taking_out_vloc'
          ? 'taking_out_vloc'
          : 'vloc_application',
    },
  });

  const moneyInTheBankEvent = usePressableAnalytics({
    analyticsId: 'goToHomeClicked',
    customAttributes: {
      cta: 'Done',
      subcategory:
        takeOutSubCategory === 'taking_out_vloc'
          ? 'taking_out_vloc'
          : 'vloc_application',
    },
  });

  const { refreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_CHECKING,
    HomeTileTypeIds.VLOC_ACCOUNT_CARD_SQUARE_TILE,
    HomeTileTypeIds.VXL_SIDE_BY_SIDE_TILE_ACCOUNT_CARD,
    HomeTileTypeIds.VXL_TRACKER_CARD_TILE_CREDIT_TRACKER,
  ]);
  const { requestedCard, setRequestedCardId, logCustomEvent } = useEngagement();

  useEffect(() => {
    logCustomEvent(
      'BRAZETRIGGER, VLOC Money in the bank success screen viewed',
    );
    requestContentCardsRefresh();
    setRequestedCardId?.('money_in_the_bank_success_screen_offer');
  }, [logCustomEvent, setRequestedCardId]);

  useEffect(() => {
    refreshTiles();
  }, [refreshTiles]);

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: false,
          gestureEnabled: false,
        }}
      />
      <SuccessScreen
        primaryButton={{
          label: translatedText('button'),
          onPress: goHome,
        }}
        visual="CASH_B_ANIMATED"
        titleText={moneyInTheBankTitle ?? ''}
        content={
          <BodyBaseText style={styles.contentText} numberOfLines={0}>
            {moneyInTheBankMessage ?? ''}
          </BodyBaseText>
        }
        productOfferCard={requestedCard}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  contentText: {
    textAlign: 'center',
  },
});

export default MoneyInTheBankScreen;
